import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, FormHelperText, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { memo, useCallback, useMemo, useState } from 'react';

import { Autocomplete, AutocompleteOption } from '~/components/Autocomplete';
import { InvestorsTable } from '~/components/InvestorsTable';
import { useGetInvestorsQuery } from '~/services/rtkQuery/company';
import { CreateCompanyFormValues } from '~/types/formValues';
import { calculateInvestorTotalFieldCount } from '~/utils/calculations';

import styles from './styles.module.scss';

interface Props {
  companyLocked: boolean;
  roundIndex: number;
  subroundIndex: number;
}

const Component: React.FC<Props> = ({ companyLocked, roundIndex, subroundIndex }) => {
  const formik = useFormikContext<CreateCompanyFormValues>();
  const [investorsSearchValue, setInvestorsSearchValue] = useState('');
  const investorsQuery = useGetInvestorsQuery(
    {
      query: investorsSearchValue.trim(),
    },
    { skip: investorsSearchValue.trim().length < 2 },
  );
  const investorsFieldPath =
    `form.rounds.${roundIndex}.subrounds.${subroundIndex}.investors` as const;
  const subroundValues = formik.values.form.rounds[roundIndex].subrounds[subroundIndex];
  // @ts-expect-error nested object type issue
  const subroundErrors = formik.errors.form?.rounds?.[roundIndex]?.subrounds?.[subroundIndex];
  const subroundTouched = formik.touched.form?.rounds?.[roundIndex]?.subrounds?.[subroundIndex];
  const [selectInvestorsModalVisible, setSelectInvestorsModalVisible] = useState(false);
  const [selectedInvestorsOptions, setSelectedInvestorsOptions] = useState<AutocompleteOption[]>(
    [],
  );

  const showSelectInvestorsModal = useCallback(() => {
    setSelectedInvestorsOptions(
      subroundValues.investors.map(investor => ({ label: investor.name, value: investor.id })),
    );

    setSelectInvestorsModalVisible(true);
  }, [subroundValues.investors]);

  const hideSelectInvestorsModal = useCallback(() => {
    setSelectInvestorsModalVisible(false);
  }, []);

  const handleSelectedInvestorsOptionsChange = useCallback(
    (value: AutocompleteOption | AutocompleteOption[]) => {
      if (Array.isArray(value)) {
        setSelectedInvestorsOptions(value);
      }
    },
    [],
  );

  // @ts-expect-error workaround for react-hook-form
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allRoundsInvestors = useMemo(() => {
    return formik.values.form.rounds
      .map(round => round.subrounds)
      .flat()
      .map(subround => subround.investors)
      .flat();
  });

  const handleSelectedInvestorsOptionsSave = useCallback(() => {
    formik.setFieldValue(
      investorsFieldPath,
      selectedInvestorsOptions.map(selectedInvestor => {
        const existingInvestor = subroundValues.investors.find(
          investor => investor.id === selectedInvestor.value,
        );

        if (existingInvestor) {
          return existingInvestor;
        }

        const newInvestor = !allRoundsInvestors.find(
          investor => investor.id === selectedInvestor.value,
        );

        return {
          id: selectedInvestor.value,
          // @ts-expect-error meta will have pbId if meta exists
          pbId: selectedInvestor.meta?.pbId,
          name: selectedInvestor.label,
          lead: false,
          new: newInvestor,
          corporate: false,
        };
      }),
    );

    hideSelectInvestorsModal();
  }, [
    allRoundsInvestors,
    formik,
    hideSelectInvestorsModal,
    investorsFieldPath,
    selectedInvestorsOptions,
    subroundValues.investors,
  ]);

  const handleInvestorsTableChange = useCallback(
    (id: number | string, field: string, checked: boolean) => {
      const index = subroundValues.investors.findIndex(investor => investor.id === id);

      formik.setFieldValue(`${investorsFieldPath}.${index}.${field}`, checked);
    },
    [formik, investorsFieldPath, subroundValues.investors],
  );

  const investorOptions = useMemo<AutocompleteOption[]>(() => {
    const queryInvestorsOptions: AutocompleteOption[] =
      investorsQuery.currentData?.result.data.map(result => ({
        label: result.investorname,
        value: result.investorid,
        meta: result.pbId ? { pbId: result.pbId } : undefined,
      })) || [];

    const allRoundsInvestorsOptions: AutocompleteOption[] = [];

    allRoundsInvestors.forEach(investor => {
      const existingInvestor = [...queryInvestorsOptions, ...allRoundsInvestorsOptions].find(
        investorOption => investorOption.value === investor.id,
      );

      if (!existingInvestor) {
        allRoundsInvestorsOptions.push({
          label: investor.name,
          value: investor.id,
          meta: investor.pbId ? { pbId: investor.pbId } : undefined,
        });
      }
    });

    return [...queryInvestorsOptions, ...allRoundsInvestorsOptions];
  }, [allRoundsInvestors, investorsQuery.currentData?.result]);

  return (
    <>
      <div>
        <div className={styles.investors__heading}>
          <div>
            <Typography color="text.primary" fontWeight="700" mt="28px" variant="subtitle1">
              Investors{' '}
              <Typography component="span" variant="subtitle1">
                (Total {subroundValues.investors.length})
              </Typography>
            </Typography>
            {subroundTouched?.investors && subroundErrors?.investors && (
              <FormHelperText error variant="outlined">
                {subroundErrors.investors}
              </FormHelperText>
            )}
          </div>
          <Button
            color="secondary"
            disabled={companyLocked}
            size="small"
            startIcon={<AddIcon sx={{ width: '24px', height: '24px' }} />}
            sx={{ width: '146px', pl: '8px', pr: '8px', fontSize: '14px' }}
            onClick={showSelectInvestorsModal}
          >
            Select Investor
          </Button>
        </div>
        <InvestorsTable
          data={subroundValues.investors}
          totalCorporate={calculateInvestorTotalFieldCount(subroundValues.investors, 'corporate')}
          totalFollowOn={
            subroundValues.investors.length -
            calculateInvestorTotalFieldCount(subroundValues.investors, 'new')
          }
          totalLeads={calculateInvestorTotalFieldCount(subroundValues.investors, 'lead')}
          totalNew={calculateInvestorTotalFieldCount(subroundValues.investors, 'new')}
          onBlur={(index, field) =>
            formik.setFieldTouched(`${investorsFieldPath}.${index}.${field}`)
          }
          onChange={companyLocked ? undefined : handleInvestorsTableChange}
        />
      </div>
      <Dialog maxWidth="md" open={selectInvestorsModalVisible} onClose={hideSelectInvestorsModal}>
        <div className={styles['select-investors-modal']}>
          <button
            aria-label="close"
            className={styles['select-investors-modal__close']}
            type="button"
            onClick={hideSelectInvestorsModal}
          >
            <CloseIcon sx={{ width: '18px', height: '18px', color: 'var(--gray-2-color)' }} />
          </button>
          <Typography color="text.primary" mb="45px" variant="h5">
            Select Investors
          </Typography>
          <Autocomplete
            creatable
            disableClearable
            multiple
            loading={investorsQuery.isFetching}
            options={investorOptions}
            placeholder="Investors*"
            value={selectedInvestorsOptions}
            onChange={handleSelectedInvestorsOptionsChange}
            onSearchValueChange={setInvestorsSearchValue}
          />
          <div className={styles['select-investors-modal__buttons']}>
            <Button
              color="secondary"
              sx={{ width: '298px', color: 'var(--black-color)' }}
              onClick={hideSelectInvestorsModal}
            >
              Cancel
            </Button>
            <Button sx={{ width: '298px' }} onClick={handleSelectedInvestorsOptionsSave}>
              Save
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export const Investors = memo(Component);
